import type { Scene } from "@babylonjs/core/scene";
import type { Camera } from "@babylonjs/core/Cameras/camera";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Constants } from "@babylonjs/core/Engines/constants";
import type { CharacterManifest } from "@arena/shared";
import { MUSEUM_BENCHES, MUSEUM_CASES, MUSEUM_DESK, MUSEUM_STATUES, type MuseumObject } from "@arena/arena-data";
import { StatueSprite } from "./spriteFighter.ts";

/** Who holds each champion: its statue is gone from the pedestal. */
export type Holders = ReadonlyMap<string, string>;

/** A fighter standing in the museum: its feet, and how tall it is drawn. */
export interface Standing {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly height: number;
}

interface Plaque {
  readonly el: HTMLDivElement;
  readonly at: Vector3;
  /** Its size on screen, measured when its text changes. */
  width: number;
  height: number;
}

/** The museum's PixelLab art (tools/pixellab/pack-museo.py). */
const ART = "/proto2d/museo";
/** The hall's inside, between the walls (packages/arena-data/src/museum.ts). */
const HALL = { minX: -13, maxX: 13, minZ: -208, maxZ: -192 } as const;
/** One tile of floor or wall covers this many metres. */
const TILE = 2;
/** The wall's two bands at their images' own proportions: wainscot and damask, then damask and cornice. */
const WALL_BANDS = [["wall-low", 0, 4.56], ["wall-high", 4.56, 3.33]] as const;
const WALL_TOP = 4.56 + 3.33;
/** The walls are this thick (outwards, the hall keeps its floor), and the front rail this deep and tall. */
const WALL_THICKNESS = 0.5;
const RAIL = { depth: 0.4, height: 1.3 } as const;
/** The night watchman's snore: 8 frames of 128 x 128. */
const GUARD_FRAMES = 8;

interface Flame {
  readonly mesh: Mesh;
  readonly phase: number;
}

/**
 * The museum at night (D49, D52), drawn with its PixelLab art: a marble floor
 * with a crimson runner, damask walls with a gilded cornice, an arched niche
 * behind every statue and round windows with the moon above them, the
 * curator's desk (rules and map), benches,
 * glass cases, candelabra, an armour, a palm and the night watchman asleep by
 * the door. No real lights (phones): moonlit pools in front of the statues,
 * cones of light over them, warm pools under the candles, all additive
 * decals, and dust in the air. The solids stay the simulation's (`MUSEUM`):
 * everything here is drawing.
 */
export class MuseumView {
  private readonly scene: Scene;
  private readonly statues: StatueSprite[] = [];
  private readonly champions: readonly CharacterManifest[];
  private readonly plaques: Plaque[] = [];
  private readonly marker: Mesh;
  private readonly markerMaterial: StandardMaterial;
  private readonly flames: Flame[] = [];
  private readonly guard: Texture;
  /** The night outside (D52): a starry sky behind everything, clouds drifting under the hall. */
  private readonly sky: Mesh;
  private readonly clouds: { mesh: Mesh; speed: number }[] = [];
  private time = 0;
  /** The plaque of the object within reach (the desk's is the last), -1 for none. */
  private near = -1;

  constructor(scene: Scene, labels: HTMLElement, champions: readonly CharacterManifest[]) {
    this.scene = scene;
    this.champions = champions;
    const glow = glowTexture(scene);
    this.hall();

    MUSEUM_STATUES.forEach((spot, i) => {
      const champion = champions[i];
      // A niche in the wall behind, the pedestal, a cone of moonlight, and the statue (or its sheet) on top.
      wallCard(scene, "niche", 1.8, spot.x, 0.5, HALL.maxZ - 0.03);
      this.card("pedestal", 1.05, spot.x, 0, spot.z - 0.25);
      decal(scene, glow, new Color3(0.55, 0.62, 0.85), 1.4, spot.x, spot.z - 1.1, 0.25);
      this.cone(spot.x, spot.y, spot.z - 0.02);
      const plaqueAt = new Vector3(spot.x, spot.y + 0.05, spot.z - 0.75);
      if (champion) {
        this.statues.push(new StatueSprite(scene, champion.id, spot.x, spot.y, spot.z));
        this.plaques.push(plaque(labels, plaqueAt, champion.displayName, "libero"));
      } else {
        this.card("veiled", 0.95, spot.x, spot.y, spot.z);
        this.plaques.push(plaque(labels, plaqueAt, "Prossimamente", ""));
      }
    });
    for (const x of [-10, -6, -2, 2, 6, 10]) wallCard(scene, "oculus", 1.4, x, 5.2, HALL.maxZ - 0.03, 0.6);

    // The curator's desk, where the host keeps the rules and the map.
    this.card("desk", 2.8, MUSEUM_DESK.x, 0, MUSEUM_DESK.z - 0.1);
    decal(scene, glow, new Color3(1, 0.78, 0.4), 2.4, MUSEUM_DESK.x + 0.4, MUSEUM_DESK.z - 0.9, 0.28);
    this.plaques.push(plaque(labels, new Vector3(MUSEUM_DESK.x, 1.9, MUSEUM_DESK.z), "Registro del curatore", "regole e mappa della stanza"));

    // Furniture: benches and cases are solid in the simulation too.
    for (const b of MUSEUM_BENCHES) this.card("bench", 2.3, b.x, 0, b.z);
    for (const c of MUSEUM_CASES) this.card("case", 1.6, c.x, 0, c.z);
    this.card("armor", 0.8, HALL.minX + 1, 0, HALL.minZ + 1.1);
    this.card("palm", 1.1, HALL.maxX - 0.9, 0, HALL.minZ + 1.0);
    for (const [x, z] of [[HALL.minX + 0.8, -195], [HALL.maxX - 0.8, -195], [HALL.minX + 0.8, -204], [HALL.maxX - 0.8, -204]] as const) {
      this.card("candelabra", 0.85, x, 0, z);
      decal(scene, glow, new Color3(1, 0.7, 0.35), 3.2, x, z, 0.25);
      this.flames.push({ mesh: this.glowSprite(glow, new Color3(0.8, 0.55, 0.3), 0.55, x, 1.85, z - 0.05), phase: Math.random() * 10 });
    }
    this.guard = this.watchman(HALL.maxX - 2.4, HALL.minZ + 1.2);
    this.sky = this.night();
    dust(scene, glow);

    this.marker = CreateTorus("museum-use", { diameter: 1.5, thickness: 0.06, tessellation: 40 }, scene);
    this.marker.isPickable = false;
    this.markerMaterial = new StandardMaterial("museum-use", scene);
    this.markerMaterial.disableLighting = true;
    this.markerMaterial.emissiveColor = new Color3(1, 0.82, 0.35);
    this.marker.material = this.markerMaterial;
    this.marker.setEnabled(false);
  }

  update(dt: number, camera: Camera, holders: Holders, usable: MuseumObject | null): void {
    this.time += dt;
    // The sky stands far behind whatever the camera looks at, only while it looks at the museum.
    const here = camera.position.z < -150;
    this.sky.setEnabled(here);
    if (here) {
      camera.getDirectionToRef(Vector3.Forward(), spot);
      this.sky.position.copyFrom(camera.position).addInPlace(spot.scaleInPlace(105));
    }
    for (const cloud of this.clouds) {
      cloud.mesh.position.x += cloud.speed * dt;
      if (cloud.mesh.position.x > 40) cloud.mesh.position.x -= 80;
    }
    this.champions.forEach((champion, i) => {
      const statue = this.statues[i];
      if (!statue) return;
      const holder = holders.get(champion.id);
      statue.setVisible(holder === undefined);
      statue.update(camera);
      const plaque = this.plaques[i]!;
      const status = plaque.el.querySelector("span")!;
      const text = holder === undefined ? "libero" : `in prestito a ${holder}`;
      if (status.textContent !== text) {
        status.textContent = text;
        measure(plaque);
      }
      plaque.el.classList.toggle("taken", holder !== undefined);
    });
    for (const { mesh, phase } of this.flames) {
      const flicker = 0.85 + 0.1 * Math.sin(this.time * 11 + phase) + 0.06 * Math.sin(this.time * 23 + phase * 2);
      mesh.scaling.set(flicker, flicker, 1);
    }
    // Mirrored frames (he faces into the hall): the offset is the right edge of the frame.
    this.guard.uOffset = ((Math.floor(this.time * 3) % GUARD_FRAMES) + 1) / GUARD_FRAMES;
    this.near = usable === null ? -1 : usable.kind === "statue" ? usable.index : this.plaques.length - 1;
    const at = usable ? spotOf(usable) : null;
    this.marker.setEnabled(at !== null);
    if (at) {
      const pulse = 1 + Math.sin(this.time * 5) * 0.06;
      this.marker.position.set(at.x, at.y + 0.04, at.z);
      this.marker.scaling.set(pulse * at.size, 1, pulse * at.size);
    }
  }

  /**
   * Plaques are HTML like the name tags, readable on a phone. Only the one
   * of the object within reach shows: side by side, on a phone, they cover
   * each other. Out of the camera's reach (from the arena) it hides too.
   * HTML is drawn over the scene, so a plaque with a fighter in front of it
   * fades, as if behind them.
   */
  placeLabels(project: (world: Vector3, out: { x: number; y: number }) => boolean, fighters: readonly Standing[]): void {
    const point = { x: 0, y: 0 };
    const feet = { x: 0, y: 0 };
    const head = { x: 0, y: 0 };
    const covers: { z: number; left: number; right: number; top: number; bottom: number }[] = [];
    for (const f of fighters) {
      if (!project(spot.set(f.x, f.y, f.z), feet) || !project(spot.set(f.x, f.y + f.height, f.z), head)) continue;
      const half = (feet.y - head.y) * 0.32;
      covers.push({ z: f.z, left: feet.x - half, right: feet.x + half, top: head.y, bottom: feet.y });
    }
    this.plaques.forEach((plaque, i) => {
      const { el, at } = plaque;
      const visible = i === this.near && project(at, point);
      el.style.visibility = visible ? "visible" : "hidden";
      if (!visible) return;
      el.style.transform = `translate(-50%, 0) translate(${point.x.toFixed(1)}px, ${point.y.toFixed(1)}px)`;
      const left = point.x - plaque.width / 2;
      const right = point.x + plaque.width / 2;
      const bottom = point.y + plaque.height;
      const behind = covers.some((c) => c.z < at.z + 0.3 && c.left < right && c.right > left && c.top < bottom && c.bottom > point.y);
      el.classList.toggle("behind", behind);
    });
  }

  /** Floor, runner, walls and the low front rail (the front wall's solid is taller, unseen). */
  private hall(): void {
    const scene = this.scene;
    const width = HALL.maxX - HALL.minX;
    const depth = HALL.maxZ - HALL.minZ;
    const centreZ = (HALL.minZ + HALL.maxZ) / 2;
    const floor = CreateGround("museum-floor", { width, height: depth }, scene);
    floor.position.set(0, 0.002, centreZ);
    floor.isPickable = false;
    floor.material = surface(scene, "floor", width / TILE, depth / TILE);
    const runner = CreateGround("museum-runner", { width: 1.8, height: 12 }, scene);
    runner.position.set(0, 0.006, HALL.minZ + 6);
    runner.isPickable = false;
    runner.material = surface(scene, "carpet", 1, 12 / 3.6);
    // The back wall, and the side walls turned to face the hall: wainscot and damask, then the cornice.
    const walls: [number, number, number, number][] = [
      [0, HALL.maxZ, 0, width],
      [HALL.minX, centreZ, -Math.PI / 2, depth],
      [HALL.maxX, centreZ, Math.PI / 2, depth],
    ];
    for (const [x, z, turn, span] of walls) {
      for (const [band, from, height] of WALL_BANDS) {
        const wall = CreatePlane(`museum-${band}`, { width: span, height }, scene);
        wall.position.set(x, from + height / 2, z);
        wall.rotation.y = turn;
        wall.isPickable = false;
        wall.material = surface(scene, band, span / TILE, 1);
      }
    }
    // Only the wainscot of the front wall, so it never hides anyone: its face, out front, and its ledge.
    const outer = width + 2 * WALL_THICKNESS;
    const rail = CreatePlane("museum-rail", { width: outer, height: RAIL.height }, scene);
    rail.position.set(0, RAIL.height / 2, HALL.minZ - RAIL.depth);
    rail.isPickable = false;
    rail.material = surface(scene, "wall-low", outer / TILE, 0.33);
    // Thickness: a walnut coping on top of every wall and of the rail, and the side walls' ends facing the camera.
    // Dark walnut, like the wainscot: a bright top would catch the bloom.
    const walnut = new StandardMaterial("museum-coping", scene);
    walnut.diffuseColor = new Color3(0.36, 0.22, 0.16);
    walnut.emissiveColor = new Color3(0.1, 0.06, 0.05);
    walnut.specularColor = Color3.Black();
    walnut.backFaceCulling = false;
    const coping = (w: number, d: number, x: number, y: number, z: number): void => {
      const top = CreateGround("museum-coping", { width: w, height: d }, scene);
      top.position.set(x, y, z);
      top.isPickable = false;
      top.material = walnut;
    };
    const sideDepth = depth + RAIL.depth;
    const sideZ = centreZ - RAIL.depth / 2;
    coping(outer, WALL_THICKNESS, 0, WALL_TOP, HALL.maxZ + WALL_THICKNESS / 2);
    coping(WALL_THICKNESS, sideDepth, HALL.minX - WALL_THICKNESS / 2, WALL_TOP, sideZ);
    coping(WALL_THICKNESS, sideDepth, HALL.maxX + WALL_THICKNESS / 2, WALL_TOP, sideZ);
    coping(outer, RAIL.depth, 0, RAIL.height, HALL.minZ - RAIL.depth / 2);
    // Close the thickness on the outside and return the inner face to the front cap.
    for (const [insideX, outsideX] of [
      [HALL.minX, HALL.minX - WALL_THICKNESS],
      [HALL.maxX, HALL.maxX + WALL_THICKNESS],
    ]) {
      for (const [x, span, z] of [
        [outsideX!, sideDepth + WALL_THICKNESS, sideZ + WALL_THICKNESS / 2],
        [insideX!, RAIL.depth, HALL.minZ - RAIL.depth / 2],
      ] as const) {
        const face = CreatePlane("museum-wall-side", { width: span, height: WALL_TOP }, scene);
        face.position.set(x, WALL_TOP / 2, z);
        face.rotation.y = Math.PI / 2;
        face.isPickable = false;
        face.material = walnut;
      }
    }
    for (const x of [HALL.minX - WALL_THICKNESS / 2, HALL.maxX + WALL_THICKNESS / 2]) {
      const end = CreatePlane("museum-wall-end", { width: WALL_THICKNESS, height: WALL_TOP }, scene);
      end.position.set(x, WALL_TOP / 2, HALL.minZ - RAIL.depth - 0.005);
      end.isPickable = false;
      end.material = walnut;
    }
  }

  /**
   * Outside the hall (D52): it floats in the night like a diorama. Its marble
   * foundation shows under the front rail and the sides, and sinks into clouds
   * that drift below; behind everything a starry sky, out of the fog, follows
   * the camera so it has no edge.
   */
  private night(): Mesh {
    const scene = this.scene;
    const width = HALL.maxX - HALL.minX + 2 * WALL_THICKNESS;
    const depth = HALL.maxZ - HALL.minZ + RAIL.depth;
    const centreZ = (HALL.minZ + HALL.maxZ) / 2 - RAIL.depth / 2;
    const band = 2.2;
    // Flush under the rail's face and the walls' outer sides, so no sky shows between hall and foundation.
    const front = HALL.minZ - RAIL.depth - 0.01;
    const left = HALL.minX - WALL_THICKNESS - 0.01;
    const right = HALL.maxX + WALL_THICKNESS + 0.01;
    for (const [x, z, turn, span] of [[0, front, 0, width], [left, centreZ, -Math.PI / 2, depth], [right, centreZ, Math.PI / 2, depth]] as const) {
      const face = CreatePlane("museum-foundation", { width: span, height: band }, scene);
      face.position.set(x, -band / 2 + 0.02, z);
      face.rotation.y = turn;
      face.isPickable = false;
      face.material = surface(scene, "foundation", span / 2.9, 1);
    }
    const sky = CreatePlane("museum-sky", { width: 420, height: 260 }, scene);
    sky.billboardMode = Mesh.BILLBOARDMODE_ALL;
    sky.isPickable = false;
    const stars = new Texture(`${ART}/sky.png`, scene, true, true, Texture.NEAREST_SAMPLINGMODE);
    stars.uScale = 26;
    stars.vScale = 16;
    const skyMaterial = new StandardMaterial("museum-sky", scene);
    skyMaterial.emissiveTexture = stars;
    skyMaterial.disableLighting = true;
    skyMaterial.fogEnabled = false;
    skyMaterial.backFaceCulling = false;
    sky.material = skyMaterial;
    // Clouds in two layers under the hall, the nearer ones faster.
    for (let i = 0; i < 14; i++) {
      const near = i % 2 === 0;
      const cloud = CreatePlane("museum-cloud", { size: 1 }, scene);
      cloud.billboardMode = Mesh.BILLBOARDMODE_Y;
      cloud.isPickable = false;
      const size = near ? 9 + (i % 3) * 2 : 14 + (i % 4) * 2;
      cloud.scaling.set(size, size * 0.4, 1);
      cloud.position.set(-40 + i * 6, near ? -3.2 - (i % 3) * 0.6 : -6.5, near ? HALL.minZ - 3 - (i % 4) * 2 : HALL.minZ + 4 + (i % 5) * 3);
      const material = new StandardMaterial("museum-cloud", scene);
      material.diffuseTexture = new Texture(`${ART}/${i % 3 === 0 ? "cloud-b" : "cloud-a"}.png`, scene, true, true, Texture.NEAREST_SAMPLINGMODE);
      material.diffuseTexture.hasAlpha = true;
      material.useAlphaFromDiffuseTexture = true;
      material.disableLighting = true;
      material.emissiveColor = new Color3(0.75, 0.8, 1);
      material.alpha = near ? 0.9 : 0.6;
      material.fogEnabled = false;
      material.backFaceCulling = false;
      cloud.material = material;
      this.clouds.push({ mesh: cloud, speed: near ? 0.5 : 0.25 });
    }
    return sky;
  }

  /** A picture standing on the floor (or on top of something), turning to the camera; its height follows the image. */
  private card(file: string, width: number, x: number, y: number, z: number): Mesh {
    const mesh = CreatePlane(`museum-${file}`, { size: 1 }, this.scene);
    mesh.bakeTransformIntoVertices(Matrix.Translation(0, 0.5, 0));
    mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
    mesh.isPickable = false;
    mesh.position.set(x, y, z);
    mesh.scaling.set(width, width, 1);
    mesh.material = picture(this.scene, file, 0.3, (w, h) => mesh.scaling.set(width, (width * h) / w, 1));
    return mesh;
  }

  /** A cone of moonlight falling on a statue. */
  private cone(x: number, y: number, z: number): void {
    const mesh = CreatePlane("museum-cone", { width: 1.7, height: 4.6 }, this.scene);
    mesh.bakeTransformIntoVertices(Matrix.Translation(0, 2.3, 0));
    mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
    mesh.position.set(x, y - 0.1, z);
    mesh.isPickable = false;
    const material = additive(this.scene, "museum-cone", coneTexture(this.scene), new Color3(0.5, 0.58, 0.8));
    material.alpha = 0.2;
    mesh.material = material;
  }

  /** A soft glow that always faces the camera (a candle's light). */
  private glowSprite(glow: Texture, color: Color3, size: number, x: number, y: number, z: number): Mesh {
    const mesh = CreatePlane("museum-flame", { size }, this.scene);
    mesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
    mesh.position.set(x, y, z);
    mesh.isPickable = false;
    mesh.material = additive(this.scene, "museum-flame", glow, color);
    return mesh;
  }

  /** The night watchman asleep on his chair by the door, snoring (8 frames), facing into the hall. */
  private watchman(x: number, z: number): Texture {
    const mesh = CreatePlane("museum-guard", { size: 1 }, this.scene);
    mesh.bakeTransformIntoVertices(Matrix.Translation(0, 0.5, 0));
    mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
    mesh.isPickable = false;
    // The champions' scale (100 px = 2.34 m), the chair's legs on row 119 of 128.
    const size = (128 * 1.8 * 1.3) / 100;
    mesh.scaling.set(size, size, 1);
    mesh.position.set(x, -(9 * size) / 128, z);
    const material = picture(this.scene, "guard", 0.3);
    const texture = material.diffuseTexture as Texture;
    texture.uScale = -1 / GUARD_FRAMES;
    texture.uOffset = 1 / GUARD_FRAMES;
    mesh.material = material;
    return texture;
  }
}

const spot = new Vector3();

/** Where the ring of a usable object goes, and how wide. */
function spotOf(object: MuseumObject): { x: number; y: number; z: number; size: number } {
  if (object.kind === "statue") {
    const s = MUSEUM_STATUES[object.index]!;
    return { x: s.x, y: 0, z: s.z - 1.05, size: 0.8 };
  }
  return { x: MUSEUM_DESK.x, y: 0, z: MUSEUM_DESK.z - 0.9, size: 1.9 };
}

/** A pixel-art image of the museum, sharp; `sized` hears its pixel size once loaded. */
function pixelTexture(scene: Scene, file: string, sized?: (width: number, height: number) => void): Texture {
  const texture: Texture = new Texture(`${ART}/${file}.png`, scene, true, true, Texture.NEAREST_SAMPLINGMODE, () => {
    const { width, height } = texture.getSize();
    sized?.(width, height);
  });
  texture.hasAlpha = true;
  return texture;
}

/** A cut-out picture lit like the fighters, `glow` keeping it readable at night. */
function picture(scene: Scene, file: string, glow: number, sized?: (width: number, height: number) => void): StandardMaterial {
  const material = new StandardMaterial(`museum-${file}`, scene);
  material.diffuseTexture = pixelTexture(scene, file, sized);
  material.useAlphaFromDiffuseTexture = true;
  material.transparencyMode = StandardMaterial.MATERIAL_ALPHATEST;
  material.alphaCutOff = 0.5;
  material.emissiveColor = new Color3(glow, glow, glow);
  material.specularColor = Color3.Black();
  material.backFaceCulling = false;
  return material;
}

/** A tiled surface (floor, wall, runner): `u` and `v` tiles across it. */
function surface(scene: Scene, file: string, u: number, v: number): StandardMaterial {
  const texture = new Texture(`${ART}/${file}.png`, scene, true, true, Texture.NEAREST_SAMPLINGMODE);
  texture.uScale = u;
  texture.vScale = v;
  const material = new StandardMaterial(`museum-${file}`, scene);
  material.diffuseTexture = texture;
  material.emissiveColor = new Color3(0.22, 0.22, 0.26);
  material.specularColor = Color3.Black();
  material.backFaceCulling = false;
  return material;
}

/** Something hung flat on the back wall (a niche, a window), its foot at `y`; `glow` lights it from within. */
function wallCard(scene: Scene, file: string, width: number, x: number, y: number, z: number, glow = 0.3): void {
  const mesh = CreatePlane(`museum-${file}`, { size: 1 }, scene);
  mesh.bakeTransformIntoVertices(Matrix.Translation(0, 0.5, 0));
  mesh.isPickable = false;
  mesh.position.set(x, y, z);
  mesh.scaling.set(width, width, 1);
  mesh.material = picture(scene, file, glow, (w, h) => mesh.scaling.set(width, (width * h) / w, 1));
}

/** A pool of light on the floor. */
function decal(scene: Scene, glow: Texture, color: Color3, radius: number, x: number, z: number, strength: number): void {
  const mesh = CreateGround("museum-pool", { width: radius * 2, height: radius * 2 }, scene);
  mesh.position.set(x, 0.01, z);
  mesh.isPickable = false;
  const material = additive(scene, "museum-pool", glow, color);
  material.alpha = strength;
  mesh.material = material;
}

function additive(scene: Scene, name: string, texture: Texture, color: Color3): StandardMaterial {
  const material = new StandardMaterial(name, scene);
  material.diffuseTexture = texture;
  material.opacityTexture = texture;
  material.disableLighting = true;
  material.emissiveColor = color;
  material.alphaMode = Constants.ALPHA_ADD;
  material.backFaceCulling = false;
  material.disableDepthWrite = true;
  return material;
}

/** A soft round spot: white in the middle, nothing at the edge. */
function glowTexture(scene: Scene): DynamicTexture {
  const size = 64;
  const texture = new DynamicTexture("museum-glow", { width: size, height: size }, scene, false);
  const ctx = texture.getContext() as CanvasRenderingContext2D;
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, "rgba(255,255,255,1)");
  gradient.addColorStop(0.4, "rgba(255,255,255,0.45)");
  gradient.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  texture.update();
  texture.hasAlpha = true;
  return texture;
}

/** A cone of light: narrow and bright at the top, wide and faint where it reaches the statue. */
function coneTexture(scene: Scene): DynamicTexture {
  const w = 64;
  const h = 128;
  const texture = new DynamicTexture("museum-cone", { width: w, height: h }, scene, false);
  const ctx = texture.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, w, h);
  // The image's top is the cone's foot (on the statue), its bottom the narrow source above.
  const fade = ctx.createLinearGradient(0, h, 0, 0);
  fade.addColorStop(0, "rgba(255,255,255,0.9)");
  fade.addColorStop(0.7, "rgba(255,255,255,0.25)");
  fade.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = fade;
  ctx.beginPath();
  ctx.moveTo(w * 0.4, h);
  ctx.lineTo(w * 0.6, h);
  ctx.lineTo(w, 0);
  ctx.lineTo(0, 0);
  ctx.closePath();
  ctx.fill();
  texture.update();
  texture.hasAlpha = true;
  return texture;
}

/** Dust in the moonlight, over the whole hall. */
function dust(scene: Scene, glow: Texture): void {
  const motes = new ParticleSystem("museum-dust", 120, scene);
  motes.particleTexture = glow;
  motes.emitter = new Vector3(0, 0.2, (HALL.minZ + HALL.maxZ) / 2);
  motes.minEmitBox = new Vector3(HALL.minX + 1, 0, -7);
  motes.maxEmitBox = new Vector3(HALL.maxX - 1, 5, 7);
  motes.color1 = new Color4(0.85, 0.9, 1, 0.45);
  motes.color2 = new Color4(1, 0.9, 0.75, 0.3);
  motes.colorDead = new Color4(0.8, 0.85, 1, 0);
  motes.minSize = 0.03;
  motes.maxSize = 0.07;
  motes.minLifeTime = 5;
  motes.maxLifeTime = 9;
  motes.emitRate = 16;
  motes.direction1 = new Vector3(-0.08, 0.05, -0.08);
  motes.direction2 = new Vector3(0.08, 0.2, 0.08);
  motes.minEmitPower = 0.15;
  motes.maxEmitPower = 0.3;
  motes.blendMode = ParticleSystem.BLENDMODE_ADD;
  motes.preWarmCycles = 200;
  motes.start();
}

function plaque(labels: HTMLElement, at: Vector3, title: string, status: string): Plaque {
  const el = document.createElement("div");
  el.className = "plaque";
  const name = document.createElement("b");
  name.textContent = title;
  const line = document.createElement("span");
  line.textContent = status;
  el.append(name, line);
  labels.appendChild(el);
  const made = { el, at, width: 0, height: 0 };
  measure(made);
  return made;
}

/** Read once per change of text, not every frame (a read after the frame's moves would force a layout). */
function measure(plaque: Plaque): void {
  plaque.width = plaque.el.offsetWidth;
  plaque.height = plaque.el.offsetHeight;
}
